import { useEffect, useState, type TransitionEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, mergeClasses } from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { BasicButton } from '@components/common/BasicButton';
import type { SessionNotification, ToastIntent } from '@store/slices/notificationsSlice';
import { useNotificationTileStyles } from './NotificationTile.styles';

export interface NotificationTileProps {
  notification: SessionNotification;
  onDismiss: (id: string) => void;
  onExited: (id: string) => void;
  isExiting: boolean;
  now?: number;
}

export function formatRelative(timestamp: number, now: number): { key: string; count?: number } {
  const diffMs = Math.max(0, now - timestamp);
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return { key: 'notifications.time.justNow' };
  if (diffMin < 60) return { key: 'notifications.time.minutesAgo', count: diffMin };
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return { key: 'notifications.time.hoursAgo', count: diffHr };
  const diffDay = Math.floor(diffHr / 24);
  return { key: 'notifications.time.daysAgo', count: diffDay };
}

const SEVERITY_CLASS: Record<ToastIntent, keyof ReturnType<typeof useNotificationTileStyles>> = {
  success: 'severitySuccess',
  error: 'severityError',
  warning: 'severityWarning',
  info: 'severityInfo',
};

export function NotificationTile({
  notification,
  onDismiss,
  onExited,
  isExiting,
  now,
}: NotificationTileProps): React.JSX.Element {
  const styles = useNotificationTileStyles();
  const { t } = useTranslation('header');
  const [currentNow, setCurrentNow] = useState<number>(now ?? Date.now());

  useEffect(() => {
    if (now !== undefined) {
      setCurrentNow(now);
      return undefined;
    }
    const id = window.setInterval(() => setCurrentNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, [now]);

  const relative = formatRelative(notification.timestamp, currentNow);
  const severityClassKey = SEVERITY_CLASS[notification.intent];
  const severityClass = styles[severityClassKey];

  const handleTransitionEnd = (e: TransitionEvent<HTMLDivElement>) => {
    if (!isExiting) return;
    if (e.propertyName !== 'max-height') return;
    onExited(notification.id);
  };

  const handleClose = () => {
    if (isExiting) return;
    onDismiss(notification.id);
  };

  return (
    <div
      data-testid={`notification-tile-${notification.id}`}
      data-intent={notification.intent}
      data-exiting={isExiting ? 'true' : 'false'}
      className={mergeClasses(styles.root, isExiting && styles.exiting)}
      onTransitionEnd={handleTransitionEnd}
      role="listitem"
    >
      <div
        className={mergeClasses(styles.severityBar, severityClass)}
        aria-hidden="true"
        data-testid={`notification-tile-severity-${notification.id}`}
      />
      <div className={styles.content}>
        <Text className={styles.message}>{notification.message}</Text>
        <Text className={styles.timestamp}>
          {t(relative.key, relative.count !== undefined ? { count: relative.count } : undefined)}
        </Text>
      </div>
      <BasicButton
        variant="subtle"
        className={styles.closeButton}
        aria-label={t('notifications.dismiss')}
        icon={<Dismiss20Regular />}
        onClick={handleClose}
        data-testid={`notification-tile-close-${notification.id}`}
      />
    </div>
  );
}

NotificationTile.displayName = 'NotificationTile';
